import { supabase } from '../lib/supabase';
import { 
  Conta, 
  Cobranca, 
  GatewayPagamento, 
  Banco, 
  DadosBancarios, 
  TipoConta, 
  FormaPagamento, 
  Categoria, 
  BaixaPagamentoPayload,
  ContaAnexo,
  TipoAnexo,
  TipoAnexoId
} from '../types/financeiro';
import { enrichConta, buildContaObs, parseContaObs } from '../utils/financeiroMetadataUtils';
import { 
  sanitizeText, 
  sanitizeConta, 
  sanitizeCategoria, 
  sanitizeFormaPagamento, 
  sanitizeBanco, 
  sanitizeTipoConta, 
  sanitizeCobranca 
} from '../utils/textSanitizer';

function calculateDateParts(dateStr?: string | null) {
  if (!dateStr) return {};
  try {
    const d = new Date(dateStr + 'T00:00:00');
    if (isNaN(d.getTime())) return {};
    const mes = d.getMonth() + 1;
    const ano = d.getFullYear();
    const ano_mes = Number(`${ano}${mes.toString().padStart(2, '0')}`);
    return { mes, ano, ano_mes };
  } catch {
    return {};
  }
}

export const financeiroService = {
  async getContas(options?: { maxRecords?: number; step?: number }): Promise<Conta[]> {
    const contasMap = new Map<number, any>();
    let from = 0;
    const step = options?.step || 1000;
    const maxRecords = options?.maxRecords || 10000; // Limite de segurança para evitar loop infinito
    
    while (from < maxRecords) {
      let { data, error } = await supabase
        .from('conta')
        .select('*, conta_anexo(id, tipo_anexo_id, descricao, arquivo)')
        .order('id', { ascending: false })
        .range(from, from + step - 1);

      if (error) {
        // Fallback retry without join or order if join fails
        const retry = await supabase
          .from('conta')
          .select('*')
          .order('id', { ascending: false })
          .range(from, from + step - 1);
        
        if (retry.error) {
          const simpleRetry = await supabase
            .from('conta')
            .select('*')
            .range(from, from + step - 1);
          data = simpleRetry.data;
          error = simpleRetry.error;
        } else {
          data = retry.data;
          error = null;
        }
      }

      if (error) {
        console.warn(`[getContas] Warning fetching page at offset ${from}:`, error.message);
        break;
      }
      
      if (data && data.length > 0) {
        for (const item of data) {
          if (item && item.id != null) {
            contasMap.set(Number(item.id), item);
          }
        }
        if (data.length < step) {
          break; // Last page
        }
        from += step;
      } else {
        break;
      }
    }
    
    // Sort descending by id
    const uniqueContas = Array.from(contasMap.values()).sort((a, b) => Number(b.id) - Number(a.id));
    return uniqueContas.map(enrichConta).map(sanitizeConta);
  },

  async createConta(contaData: Omit<Conta, 'id'> & { novos_anexos?: Array<{ tipo_anexo_id: number; descricao: string; arquivo: string }> }): Promise<Conta> {
    const vencParts = calculateDateParts(contaData.dt_vencimento);
    const emisParts = calculateDateParts(contaData.dt_emissao);
    const pagParts = calculateDateParts(contaData.dt_pagamento);

    // Keep clean pure obs and structured metadata
    const parsed = parseContaObs(contaData.obs);
    const rawCleanObs = parsed.observacao_pura || contaData.obs || null;
    const cleanObs = rawCleanObs ? sanitizeText(rawCleanObs) : null;
    const structuredObs = buildContaObs(cleanObs || '', {
      numero_nf: contaData.numero_nf || parsed.numero_nf || undefined,
      nf_url: contaData.nf_url || parsed.nf_url || undefined,
      nf_nome: contaData.nf_nome || parsed.nf_nome || undefined,
      boleto_url: contaData.boleto_url || parsed.boleto_url || undefined,
      boleto_nome: contaData.boleto_nome || parsed.boleto_nome || undefined,
      comprovante_url: contaData.comprovante_url || parsed.comprovante_url || undefined,
      comprovante_nome: parsed.comprovante_nome || undefined,
      valor_pago: contaData.valor_pago || parsed.valor_pago || undefined,
      banco_id: contaData.banco_id || parsed.banco_id || undefined,
      banco_nome: contaData.banco_nome || parsed.banco_nome || undefined,
      cnpj_faturador: contaData.cnpj_faturador || parsed.cnpj_faturador || undefined,
      empresa_faturadora: contaData.empresa_faturadora || parsed.empresa_faturadora || undefined,
    });

    const payload: any = {
      tipo_conta_id: contaData.tipo_conta_id,
      pessoa_id: contaData.pessoa_id,
      forma_pagamento_id: contaData.forma_pagamento_id,
      descricao: sanitizeText(contaData.descricao),
      valor: contaData.valor,
      dt_vencimento: contaData.dt_vencimento || null,
      dt_emissao: contaData.dt_emissao || null,
      dt_pagamento: contaData.dt_pagamento || null,
      parcela: contaData.parcela || 1,
      obs: structuredObs,
      // Dedicated direct columns
      numero_nf: contaData.numero_nf || parsed.numero_nf || null,
      comprovante_url: contaData.comprovante_url || parsed.comprovante_url || null,
      valor_pago: contaData.valor_pago || parsed.valor_pago || (contaData.dt_pagamento ? contaData.valor : null),
      mes_vencimento: vencParts.mes || null,
      ano_vencimento: vencParts.ano || null,
      ano_mes_vencimento: vencParts.ano_mes || null,
      mes_emissao: emisParts.mes || null,
      ano_emissao: emisParts.ano || null,
      ano_mes_emissao: emisParts.ano_mes || null,
      mes_pagamento: pagParts.mes || null,
      ano_pagamento: pagParts.ano || null,
      ano_mes_pagamento: pagParts.ano_mes || null,
    };

    if (contaData.categoria_id) payload.categoria_id = contaData.categoria_id;
    if (contaData.gateway_pagamento_id) payload.gateway_pagamento_id = contaData.gateway_pagamento_id;
    if (contaData.medicoes_id) payload.medicoes_id = contaData.medicoes_id;
    if (contaData.medicoes_agregado_id) payload.medicoes_agregado_id = contaData.medicoes_agregado_id;
    if (contaData.pedido_venda_id) payload.pedido_venda_id = contaData.pedido_venda_id;

    let createdConta: Conta;

    try {
      const { data, error } = await supabase
        .from('conta')
        .insert([payload])
        .select()
        .single();

      if (error) throw error;
      createdConta = enrichConta(data);
    } catch (insertErr: any) {
      // If error is about missing columns (before SQL script was executed), fallback gracefully
      if (insertErr?.code === '42703' || insertErr?.message?.includes('does not exist')) {
        console.warn('Dedicated column not found in database yet, falling back:', insertErr.message);
        const fallbackPayload = { ...payload };
        delete fallbackPayload.numero_nf;
        delete fallbackPayload.comprovante_url;
        delete fallbackPayload.valor_pago;
        // store in obs fallback
        fallbackPayload.obs = buildContaObs(cleanObs || '', {
          numero_nf: contaData.numero_nf || undefined,
          nf_url: contaData.nf_url || undefined,
          boleto_url: contaData.boleto_url || undefined,
          comprovante_url: contaData.comprovante_url || undefined,
          valor_pago: contaData.valor_pago || undefined,
        });

        const { data, error } = await supabase
          .from('conta')
          .insert([fallbackPayload])
          .select()
          .single();

        if (error) throw error;
        createdConta = enrichConta(data);
      } else {
        throw insertErr;
      }
    }

    // Save attachments in conta_anexo if provided
    if (createdConta?.id) {
      const anexosToInsert: Array<{ conta_id: number; tipo_anexo_id: number; descricao: string; arquivo: string }> = [];

      if (contaData.novos_anexos && contaData.novos_anexos.length > 0) {
        contaData.novos_anexos.forEach((a) => {
          if (a.arquivo) {
            anexosToInsert.push({
              conta_id: createdConta.id,
              tipo_anexo_id: a.tipo_anexo_id || TipoAnexoId.OUTROS,
              descricao: a.descricao || 'Anexo',
              arquivo: a.arquivo
            });
          }
        });
      }

      if (contaData.boleto_url && !anexosToInsert.some(a => a.arquivo === contaData.boleto_url)) {
        anexosToInsert.push({
          conta_id: createdConta.id,
          tipo_anexo_id: TipoAnexoId.BOLETO,
          descricao: contaData.boleto_nome || 'Boleto Bancário',
          arquivo: contaData.boleto_url
        });
      }

      if (contaData.nf_url && !anexosToInsert.some(a => a.arquivo === contaData.nf_url)) {
        anexosToInsert.push({
          conta_id: createdConta.id,
          tipo_anexo_id: TipoAnexoId.NOTA_FISCAL,
          descricao: contaData.nf_nome || `Nota Fiscal ${contaData.numero_nf || ''}`.trim(),
          arquivo: contaData.nf_url
        });
      }

      if (contaData.comprovante_url && !anexosToInsert.some(a => a.arquivo === contaData.comprovante_url)) {
        anexosToInsert.push({
          conta_id: createdConta.id,
          tipo_anexo_id: TipoAnexoId.COMPROVANTE,
          descricao: contaData.comprovante_nome || 'Comprovante de Pagamento',
          arquivo: contaData.comprovante_url
        });
      }

      if (anexosToInsert.length > 0) {
        try {
          await supabase.from('conta_anexo').insert(anexosToInsert);
        } catch (anexoErr) {
          console.warn('[createConta] Notice inserting conta_anexo:', anexoErr);
        }
      }
    }

    return createdConta;
  },

  async updateConta(id: number, contaData: Partial<Conta> & { 
    anexos?: Array<{ id?: number; tipo_anexo_id: number; descricao: string; arquivo: string; isExisting?: boolean }>;
    novos_anexos?: Array<{ tipo_anexo_id: number; descricao: string; arquivo: string }>;
  }): Promise<Conta> {
    const vencParts = calculateDateParts(contaData.dt_vencimento);
    const emisParts = calculateDateParts(contaData.dt_emissao);
    const pagParts = calculateDateParts(contaData.dt_pagamento);

    const parsed = parseContaObs(contaData.obs);
    const rawCleanObs = parsed.observacao_pura !== undefined ? parsed.observacao_pura : (contaData.obs || null);
    const cleanObs = rawCleanObs ? sanitizeText(rawCleanObs) : null;
    
    // Explicitly handle fields: if explicitly set to null, strip them from structured obs
    const targetNumeroNf = contaData.numero_nf !== undefined ? (contaData.numero_nf ? sanitizeText(contaData.numero_nf) : undefined) : parsed.numero_nf;
    const targetNfUrl = contaData.nf_url !== undefined ? (contaData.nf_url || undefined) : parsed.nf_url;
    const targetNfNome = contaData.nf_nome !== undefined ? (contaData.nf_nome || undefined) : parsed.nf_nome;
    const targetBoletoUrl = contaData.boleto_url !== undefined ? (contaData.boleto_url || undefined) : parsed.boleto_url;
    const targetBoletoNome = contaData.boleto_nome !== undefined ? (contaData.boleto_nome || undefined) : parsed.boleto_nome;
    const targetComprovanteUrl = contaData.comprovante_url !== undefined ? (contaData.comprovante_url || undefined) : parsed.comprovante_url;
    const targetComprovanteNome = contaData.comprovante_nome !== undefined ? (contaData.comprovante_nome || undefined) : parsed.comprovante_nome;

    const structuredObs = buildContaObs(cleanObs || '', {
      numero_nf: targetNumeroNf,
      nf_url: targetNfUrl,
      nf_nome: targetNfNome,
      boleto_url: targetBoletoUrl,
      boleto_nome: targetBoletoNome,
      comprovante_url: targetComprovanteUrl,
      comprovante_nome: targetComprovanteNome,
      valor_pago: contaData.valor_pago !== undefined ? contaData.valor_pago : parsed.valor_pago,
      banco_id: contaData.banco_id !== undefined ? (contaData.banco_id || undefined) : parsed.banco_id,
      banco_nome: contaData.banco_nome !== undefined ? (contaData.banco_nome || undefined) : parsed.banco_nome,
      cnpj_faturador: contaData.cnpj_faturador !== undefined ? (contaData.cnpj_faturador || undefined) : parsed.cnpj_faturador,
      empresa_faturadora: contaData.empresa_faturadora !== undefined ? (contaData.empresa_faturadora || undefined) : parsed.empresa_faturadora,
    });

    const payload: any = {
      tipo_conta_id: contaData.tipo_conta_id,
      pessoa_id: contaData.pessoa_id,
      forma_pagamento_id: contaData.forma_pagamento_id,
      descricao: contaData.descricao ? sanitizeText(contaData.descricao) : contaData.descricao,
      valor: contaData.valor,
      dt_vencimento: contaData.dt_vencimento || null,
      dt_emissao: contaData.dt_emissao || null,
      dt_pagamento: contaData.dt_pagamento || null,
      parcela: contaData.parcela || 1,
      obs: structuredObs,
      updated_at: new Date().toISOString(),
    };

    if (contaData.numero_nf !== undefined) payload.numero_nf = contaData.numero_nf ? sanitizeText(contaData.numero_nf) : null;
    if (contaData.comprovante_url !== undefined) payload.comprovante_url = contaData.comprovante_url || null;
    if (contaData.valor_pago !== undefined) payload.valor_pago = contaData.valor_pago || null;

    if (vencParts.mes) {
      payload.mes_vencimento = vencParts.mes;
      payload.ano_vencimento = vencParts.ano;
      payload.ano_mes_vencimento = vencParts.ano_mes;
    }
    if (emisParts.mes) {
      payload.mes_emissao = emisParts.mes;
      payload.ano_emissao = emisParts.ano;
      payload.ano_mes_emissao = emisParts.ano_mes;
    }
    if (pagParts.mes) {
      payload.mes_pagamento = pagParts.mes;
      payload.ano_pagamento = pagParts.ano;
      payload.ano_mes_pagamento = pagParts.ano_mes;
    } else if (contaData.dt_pagamento === null || contaData.dt_pagamento === '') {
      payload.mes_pagamento = null;
      payload.ano_pagamento = null;
      payload.ano_mes_pagamento = null;
    }

    if (contaData.categoria_id !== undefined) payload.categoria_id = contaData.categoria_id || null;
    if (contaData.gateway_pagamento_id !== undefined) payload.gateway_pagamento_id = contaData.gateway_pagamento_id || null;

    const { data, error } = await supabase
      .from('conta')
      .update(payload)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;

    // Synchronize full list of attachments if passed
    if (contaData.anexos) {
      try {
        const { data: dbRows } = await supabase
          .from('conta_anexo')
          .select('id, arquivo')
          .eq('conta_id', id);

        const currentDbRows = dbRows || [];
        const keptDbIds = new Set(
          contaData.anexos.filter(a => a.id && a.id > 0).map(a => a.id)
        );

        // 1. Delete removed attachments from database table
        for (const row of currentDbRows) {
          if (!keptDbIds.has(row.id)) {
            await supabase.from('conta_anexo').delete().eq('id', row.id);
          }
        }

        // 2. Insert new attachments (including converted legacy/synthetic attachments)
        const toInsert = contaData.anexos
          .filter(a => (!a.id || a.id <= 0 || !(a as any).isExisting) && a.arquivo)
          .map(a => ({
            conta_id: id,
            tipo_anexo_id: a.tipo_anexo_id || TipoAnexoId.OUTROS,
            descricao: a.descricao || 'Anexo',
            arquivo: a.arquivo
          }));

        if (toInsert.length > 0) {
          await supabase.from('conta_anexo').insert(toInsert);
        }

        // 3. Update existing attachments (e.g. description or type changes)
        for (const a of contaData.anexos) {
          if (a.id && a.id > 0) {
            await supabase.from('conta_anexo').update({
              tipo_anexo_id: a.tipo_anexo_id,
              descricao: a.descricao
            }).eq('id', a.id);
          }
        }
      } catch (anexoErr) {
        console.warn('[updateConta] Notice syncing conta_anexo:', anexoErr);
      }
    } else if (contaData.novos_anexos && contaData.novos_anexos.length > 0) {
      const anexosToInsert = contaData.novos_anexos.map(a => ({
        conta_id: id,
        tipo_anexo_id: a.tipo_anexo_id || TipoAnexoId.OUTROS,
        descricao: a.descricao || 'Anexo',
        arquivo: a.arquivo
      }));
      try {
        await supabase.from('conta_anexo').insert(anexosToInsert);
      } catch (anexoErr) {
        console.warn('[updateConta] Notice inserting conta_anexo:', anexoErr);
      }
    }

    return sanitizeConta(enrichConta(data));
  },

  async darBaixaConta(payload: BaixaPagamentoPayload): Promise<Conta> {
    const { 
      conta_id, 
      dt_pagamento, 
      valor_pago, 
      forma_pagamento_id, 
      comprovante_url, 
      comprovante_nome, 
      boleto_url,
      boleto_nome,
      nf_url,
      nf_nome,
      novos_anexos,
      observacao_baixa, 
      numero_nf 
    } = payload;

    // Fetch existing conta
    const { data: existing, error: fetchErr } = await supabase
      .from('conta')
      .select('*')
      .eq('id', conta_id)
      .single();

    if (fetchErr) throw fetchErr;

    const parsed = parseContaObs(existing.obs);
    const existingCleanObs = parsed.observacao_pura || (existing.obs && !existing.obs.includes('NF:') ? existing.obs : '');
    const finalCleanObs = observacao_baixa
      ? (existingCleanObs ? `${existingCleanObs}\nBaixa: ${observacao_baixa}` : `Baixa: ${observacao_baixa}`)
      : existingCleanObs;

    const pagParts = calculateDateParts(dt_pagamento);

    // IMPORTANTE: usar "??" (não "||") — um valor_pago de R$ 0 (ex.: baixa por
    // desconto integral) é um valor válido e não deve ser silenciosamente
    // substituído pelo valor cheio da conta.
    const valorPagoFinal = valor_pago ?? existing.valor_pago ?? existing.valor;

    const structuredObs = buildContaObs(finalCleanObs || '', {
      numero_nf: numero_nf || existing.numero_nf || parsed.numero_nf || undefined,
      nf_url: nf_url || parsed.nf_url || undefined,
      nf_nome: nf_nome || parsed.nf_nome || undefined,
      boleto_url: boleto_url || parsed.boleto_url || undefined,
      boleto_nome: boleto_nome || parsed.boleto_nome || undefined,
      comprovante_url: comprovante_url || existing.comprovante_url || parsed.comprovante_url || undefined,
      comprovante_nome: comprovante_nome || parsed.comprovante_nome || undefined,
      valor_pago: valorPagoFinal ?? undefined,
      banco_id: payload.banco_id || existing.banco_id || parsed.banco_id || undefined,
      banco_nome: payload.banco_nome || existing.banco_nome || parsed.banco_nome || undefined,
      cnpj_faturador: payload.cnpj_faturador || existing.cnpj_faturador || parsed.cnpj_faturador || undefined,
      empresa_faturadora: payload.empresa_faturadora || existing.empresa_faturadora || parsed.empresa_faturadora || undefined,
    });

    const updatePayload: any = {
      dt_pagamento: dt_pagamento || new Date().toISOString().split('T')[0],
      obs: structuredObs,
      numero_nf: numero_nf || existing.numero_nf || parsed.numero_nf || null,
      comprovante_url: comprovante_url || existing.comprovante_url || parsed.comprovante_url || null,
      valor_pago: valorPagoFinal,
      mes_pagamento: pagParts.mes || null,
      ano_pagamento: pagParts.ano || null,
      ano_mes_pagamento: pagParts.ano_mes || null,
      updated_at: new Date().toISOString(),
    };

    if (forma_pagamento_id) {
      updatePayload.forma_pagamento_id = forma_pagamento_id;
    }

    let updatedConta: any;

    try {
      const { data, error } = await supabase
        .from('conta')
        .update(updatePayload)
        .eq('id', conta_id)
        .select()
        .single();

      if (error) throw error;
      updatedConta = data;
    } catch (updateErr: any) {
      // Fallback if dedicated columns are not yet in SQL
      if (updateErr?.code === '42703' || updateErr?.message?.includes('does not exist')) {
        const fallbackObs = buildContaObs(finalCleanObs || '', {
          numero_nf: numero_nf || parsed.numero_nf,
          nf_url: nf_url || parsed.nf_url,
          boleto_url: boleto_url || parsed.boleto_url,
          comprovante_url: comprovante_url || parsed.comprovante_url,
          comprovante_nome: comprovante_nome || parsed.comprovante_nome,
          valor_pago: valor_pago || existing.valor,
          banco_id: payload.banco_id || existing.banco_id || parsed.banco_id,
          banco_nome: payload.banco_nome || existing.banco_nome || parsed.banco_nome,
          cnpj_faturador: payload.cnpj_faturador || existing.cnpj_faturador || parsed.cnpj_faturador,
          empresa_faturadora: payload.empresa_faturadora || existing.empresa_faturadora || parsed.empresa_faturadora,
        });

        const fallbackPayload: any = {
          dt_pagamento: dt_pagamento || new Date().toISOString().split('T')[0],
          obs: fallbackObs,
          mes_pagamento: pagParts.mes || null,
          ano_pagamento: pagParts.ano || null,
          ano_mes_pagamento: pagParts.ano_mes || null,
          updated_at: new Date().toISOString(),
        };
        if (forma_pagamento_id) fallbackPayload.forma_pagamento_id = forma_pagamento_id;

        const { data, error } = await supabase
          .from('conta')
          .update(fallbackPayload)
          .eq('id', conta_id)
          .select()
          .single();

        if (error) throw error;
        updatedConta = data;
      } else {
        throw updateErr;
      }
    }

    // Save all attachments into conta_anexo
    const anexosToInsert: Array<{ conta_id: number; tipo_anexo_id: number; descricao: string; arquivo: string }> = [];

    if (novos_anexos && novos_anexos.length > 0) {
      novos_anexos.forEach((a) => {
        if (a.arquivo) {
          anexosToInsert.push({
            conta_id: conta_id,
            tipo_anexo_id: a.tipo_anexo_id || TipoAnexoId.COMPROVANTE,
            descricao: a.descricao || 'Comprovante / Anexo',
            arquivo: a.arquivo
          });
        }
      });
    }

    if (comprovante_url && !anexosToInsert.some(a => a.arquivo === comprovante_url)) {
      anexosToInsert.push({
        conta_id: conta_id,
        tipo_anexo_id: TipoAnexoId.COMPROVANTE,
        descricao: comprovante_nome || 'Comprovante de Pagamento',
        arquivo: comprovante_url
      });
    }

    if (boleto_url && !anexosToInsert.some(a => a.arquivo === boleto_url)) {
      anexosToInsert.push({
        conta_id: conta_id,
        tipo_anexo_id: TipoAnexoId.BOLETO,
        descricao: boleto_nome || 'Boleto Bancário',
        arquivo: boleto_url
      });
    }

    if (nf_url && !anexosToInsert.some(a => a.arquivo === nf_url)) {
      anexosToInsert.push({
        conta_id: conta_id,
        tipo_anexo_id: TipoAnexoId.NOTA_FISCAL,
        descricao: nf_nome || `Nota Fiscal ${numero_nf || ''}`.trim(),
        arquivo: nf_url
      });
    }

    if (anexosToInsert.length > 0) {
      try {
        await supabase.from('conta_anexo').insert(anexosToInsert);
      } catch (anexoErr) {
        console.warn('[darBaixaConta] Notice inserting conta_anexo:', anexoErr);
      }
    }

    return enrichConta(updatedConta);
  },

  async estornarBaixaConta(id: number): Promise<Conta> {
    const { data: existing, error: fetchErr } = await supabase
      .from('conta')
      .select('*')
      .eq('id', id)
      .single();

    if (fetchErr) throw fetchErr;

    const parsed = parseContaObs(existing.obs);
    const cleanObs = parsed.observacao_pura || null;

    try {
      const { data, error } = await supabase
        .from('conta')
        .update({
          dt_pagamento: null,
          mes_pagamento: null,
          ano_pagamento: null,
          ano_mes_pagamento: null,
          valor_pago: null,
          obs: cleanObs,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return enrichConta(data);
    } catch (err: any) {
      if (err?.code === '42703' || err?.message?.includes('does not exist')) {
        const { data, error } = await supabase
          .from('conta')
          .update({
            dt_pagamento: null,
            mes_pagamento: null,
            ano_pagamento: null,
            ano_mes_pagamento: null,
            obs: cleanObs,
            updated_at: new Date().toISOString(),
          })
          .eq('id', id)
          .select()
          .single();

        if (error) throw error;
        return enrichConta(data);
      }
      throw err;
    }
  },

  async deleteConta(id: number): Promise<void> {
    // Delete any dependent cobranca records first
    try {
      await supabase.from('cobranca').delete().eq('conta_id', id);
    } catch (e) {
      console.warn('cobranca delete notice:', e);
    }

    // Delete any attached files in conta_anexo
    try {
      await supabase.from('conta_anexo').delete().eq('conta_id', id);
    } catch (e) {
      console.warn('conta_anexo delete notice:', e);
    }

    const { error } = await supabase
      .from('conta')
      .delete()
      .eq('id', id);

    if (error) throw error;
  },

  async getContaById(id: number): Promise<Conta | null> {
    const { data, error } = await supabase
      .from('conta')
      .select('*, categoria(nome), forma_pagamento(nome), tipo_conta(nome)')
      .eq('id', id)
      .single();

    if (error) return null;
    
    const enriched = enrichConta(data);
    const anexos = await this.getAnexosConta(id);
    enriched.anexos = anexos;
    enriched.anexos_count = anexos.length;
    return sanitizeConta(enriched);
  },

  // -------------------------------------------------------------
  // ANEXOS MANAGEMENT (BOLETOS, NOTAS FISCAIS, COMPROVANTES, ETC)
  // -------------------------------------------------------------

  async getTiposAnexo(): Promise<TipoAnexo[]> {
    const { data, error } = await supabase
      .from('tipo_anexo')
      .select('*')
      .order('id', { ascending: true });

    if (error || !data || data.length === 0) {
      return [
        { id: TipoAnexoId.RECIBO, nome: 'Recibo' },
        { id: TipoAnexoId.BOLETO, nome: 'Boleto' },
        { id: TipoAnexoId.NOTA_FISCAL, nome: 'Nota Fiscal' },
        { id: TipoAnexoId.FATURA, nome: 'Fatura' },
        { id: TipoAnexoId.COMPROVANTE, nome: 'Comprovante de Pagamento' },
        { id: TipoAnexoId.BM, nome: 'BM' },
        { id: TipoAnexoId.OUTROS, nome: 'Outros Documentos' }
      ];
    }

    return data.map((t) => ({
      ...t,
      nome: sanitizeText(t.nome)
    }));
  },

  async getAnexosConta(contaId: number): Promise<ContaAnexo[]> {
    const anexosList: ContaAnexo[] = [];
    const seenUrls = new Set<string>();

    try {
      const { data, error } = await supabase
        .from('conta_anexo')
        .select('*, tipo_anexo(id, nome)')
        .eq('conta_id', contaId)
        .order('id', { ascending: true });

      if (!error && data) {
        data.forEach((item: any) => {
          if (item && item.arquivo) {
            seenUrls.add(item.arquivo.trim());
            anexosList.push({
              id: item.id,
              conta_id: item.conta_id,
              tipo_anexo_id: item.tipo_anexo_id,
              descricao: sanitizeText(item.descricao) || 'Anexo',
              arquivo: item.arquivo,
              tipo_anexo: item.tipo_anexo ? {
                id: item.tipo_anexo.id,
                nome: sanitizeText(item.tipo_anexo.nome)
              } : undefined,
              created_at: item.created_at
            });
          }
        });
      }
    } catch (err) {
      console.warn('[getAnexosConta] Error fetching from conta_anexo:', err);
    }

    // Check conta table and obs metadata to incorporate any legacy or parsed attachments
    try {
      const { data: contaData } = await supabase
        .from('conta')
        .select('obs, comprovante_url, numero_nf')
        .eq('id', contaId)
        .single();

      if (contaData) {
        const meta = parseContaObs(contaData.obs);

        // Boleto from metadata
        if (meta.boleto_url && !seenUrls.has(meta.boleto_url.trim())) {
          seenUrls.add(meta.boleto_url.trim());
          anexosList.push({
            id: -2,
            conta_id: contaId,
            tipo_anexo_id: TipoAnexoId.BOLETO,
            descricao: meta.boleto_nome || 'Boleto Bancário',
            arquivo: meta.boleto_url,
            tipo_anexo: { id: TipoAnexoId.BOLETO, nome: 'Boleto' }
          });
        }

        // NF from metadata
        if (meta.nf_url && !seenUrls.has(meta.nf_url.trim())) {
          seenUrls.add(meta.nf_url.trim());
          anexosList.push({
            id: -3,
            conta_id: contaId,
            tipo_anexo_id: TipoAnexoId.NOTA_FISCAL,
            descricao: meta.nf_nome || `Nota Fiscal ${meta.numero_nf || contaData.numero_nf || ''}`.trim(),
            arquivo: meta.nf_url,
            tipo_anexo: { id: TipoAnexoId.NOTA_FISCAL, nome: 'Nota Fiscal' }
          });
        }

        // Comprovante from conta / metadata
        const compUrl = contaData.comprovante_url || meta.comprovante_url;
        if (compUrl && !seenUrls.has(compUrl.trim())) {
          seenUrls.add(compUrl.trim());
          anexosList.push({
            id: -1,
            conta_id: contaId,
            tipo_anexo_id: TipoAnexoId.COMPROVANTE,
            descricao: meta.comprovante_nome || 'Comprovante de Pagamento',
            arquivo: compUrl,
            tipo_anexo: { id: TipoAnexoId.COMPROVANTE, nome: 'Comprovante de Pagamento' }
          });
        }
      }
    } catch (metaErr) {
      console.warn('[getAnexosConta] Error checking conta metadata:', metaErr);
    }

    return anexosList;
  },

  async adicionarAnexoConta(
    contaId: number, 
    tipoAnexoId: number, 
    descricao: string, 
    arquivo: string
  ): Promise<ContaAnexo> {
    const { data, error } = await supabase
      .from('conta_anexo')
      .insert([{
        conta_id: contaId,
        tipo_anexo_id: tipoAnexoId,
        descricao: sanitizeText(descricao) || 'Anexo',
        arquivo: arquivo
      }])
      .select('*, tipo_anexo(id, nome)')
      .single();

    if (error) throw error;
    return {
      id: data.id,
      conta_id: data.conta_id,
      tipo_anexo_id: data.tipo_anexo_id,
      descricao: sanitizeText(data.descricao),
      arquivo: data.arquivo,
      tipo_anexo: data.tipo_anexo ? {
        id: data.tipo_anexo.id,
        nome: sanitizeText(data.tipo_anexo.nome)
      } : undefined
    };
  },

  async removerAnexoConta(anexoId: number, contaId?: number): Promise<boolean> {
    if (anexoId > 0) {
      const { error } = await supabase
        .from('conta_anexo')
        .delete()
        .eq('id', anexoId);

      if (error) throw error;
      return true;
    }
    
    // If it's a synthetic ID (< 0), clean it from conta metadata/comprovante_url if contaId is provided
    if (contaId && anexoId < 0) {
      try {
        const { data: conta } = await supabase.from('conta').select('obs, comprovante_url').eq('id', contaId).single();
        if (conta) {
          const meta = parseContaObs(conta.obs);
          let updatedObs = meta.observacao_pura || '';
          
          if (anexoId === -1) {
            // Remove comprovante
            await supabase.from('conta').update({
              comprovante_url: null,
              obs: buildContaObs(updatedObs, { ...meta, comprovante_url: undefined, comprovante_nome: undefined })
            }).eq('id', contaId);
          } else if (anexoId === -2) {
            // Remove boleto
            await supabase.from('conta').update({
              obs: buildContaObs(updatedObs, { ...meta, boleto_url: undefined, boleto_nome: undefined })
            }).eq('id', contaId);
          } else if (anexoId === -3) {
            // Remove NF
            await supabase.from('conta').update({
              obs: buildContaObs(updatedObs, { ...meta, nf_url: undefined, nf_nome: undefined })
            }).eq('id', contaId);
          }
        }
      } catch (err) {
        console.warn('Error clearing synthetic anexo:', err);
      }
    }

    return true;
  },

  async getCobrancas(): Promise<Cobranca[]> {
    const cobrancasMap = new Map<string, Cobranca>();
    let from = 0;
    const step = 500;
    
    while (true) {
      let { data, error } = await supabase
        .from('cobranca')
        .select('*')
        .order('id', { ascending: false })
        .range(from, from + step - 1);

      if (error) {
        // Fallback retry without order
        const retry = await supabase
          .from('cobranca')
          .select('*')
          .range(from, from + step - 1);
        data = retry.data;
        error = retry.error;
      }

      if (error) {
        console.warn(`[getCobrancas] Warning fetching page at offset ${from}:`, error.message);
        break;
      }
      
      if (data && data.length > 0) {
        for (const item of data) {
          if (item && item.id != null) {
            cobrancasMap.set(String(item.id), item);
          }
        }
        if (data.length < step) {
          break; // Last page
        }
        from += step;
      } else {
        break;
      }
    }
    return Array.from(cobrancasMap.values())
      .map(sanitizeCobranca)
      .sort((a, b) => String(b.id).localeCompare(String(a.id)));
  },

  async getGateways(): Promise<GatewayPagamento[]> {
    // A tabela `gateway_pagamento` guarda segredos (access_token, client_secret,
    // certificado_key) e por isso o navegador não tem permissão de leitura nela
    // — o select('*') falhava com "permission denied" e a tela ficava sem
    // gateways. A view `v_gateways_ativos` expõe só id e nome, que é o que a UI
    // precisa; os segredos continuam acessíveis apenas ao backend.
    const { data, error } = await supabase
      .from('v_gateways_ativos')
      .select('id, nome')
      .order('id', { ascending: true });

    if (error) {
      console.warn('[financeiroService] Erro ao carregar gateways:', error.message);
      return [];
    }
    const map = new Map<number, GatewayPagamento>();
    (data || []).forEach((g) => {
      if (g && g.id != null) map.set(Number(g.id), g);
    });
    return Array.from(map.values());
  },

  async getBancos(): Promise<Banco[]> {
    const { data, error } = await supabase
      .from('banco')
      .select('*')
      .is('deleted_at', null)
      .order('id', { ascending: true });

    if (error) {
      // Fallback if deleted_at column is not present or returns error
      const { data: fallbackData, error: fbErr } = await supabase
        .from('banco')
        .select('*')
        .order('id', { ascending: true });
      if (fbErr) return [];
      const map = new Map<number, Banco>();
      (fallbackData || []).forEach((b) => {
        if (b && b.id != null) map.set(Number(b.id), sanitizeBanco(b));
      });
      return Array.from(map.values());
    }

    const map = new Map<number, Banco>();
    (data || []).forEach((b) => {
      if (b && b.id != null) map.set(Number(b.id), sanitizeBanco(b));
    });
    return Array.from(map.values());
  },

  async createBanco(nome: string): Promise<{ success: boolean; data?: Banco; error?: string }> {
    try {
      const cleanNome = sanitizeText(nome).trim();
      if (!cleanNome) {
        return { success: false, error: 'O nome da instituição bancária é obrigatório.' };
      }

      const { data, error } = await supabase
        .from('banco')
        .insert([{ nome: cleanNome }])
        .select()
        .single();

      if (error) {
        console.error('Erro ao cadastrar banco:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data: sanitizeBanco(data) };
    } catch (e: any) {
      console.error('Erro inesperado ao criar banco:', e);
      return { success: false, error: e.message || 'Erro inesperado ao criar banco.' };
    }
  },

  async updateBanco(id: number, nome: string): Promise<{ success: boolean; data?: Banco; error?: string }> {
    try {
      const cleanNome = sanitizeText(nome).trim();
      if (!cleanNome) {
        return { success: false, error: 'O nome da instituição bancária é obrigatório.' };
      }

      const { data, error } = await supabase
        .from('banco')
        .update({ nome: cleanNome, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Erro ao atualizar banco:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data: sanitizeBanco(data) };
    } catch (e: any) {
      console.error('Erro inesperado ao atualizar banco:', e);
      return { success: false, error: e.message || 'Erro inesperado ao atualizar banco.' };
    }
  },

  async deleteBanco(id: number): Promise<{ success: boolean; error?: string }> {
    try {
      // Tenta soft delete primeiro ou exclusão direta
      const { error: softErr } = await supabase
        .from('banco')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', id);

      if (softErr) {
        // Tenta delete direto
        const { error: delErr } = await supabase
          .from('banco')
          .delete()
          .eq('id', id);

        if (delErr) {
          console.error('Erro ao excluir banco:', delErr);
          return { success: false, error: delErr.message };
        }
      }

      return { success: true };
    } catch (e: any) {
      console.error('Erro inesperado ao excluir banco:', e);
      return { success: false, error: e.message || 'Erro inesperado ao excluir banco.' };
    }
  },

  async getDadosBancariosByPessoa(pessoaId: number): Promise<DadosBancarios[]> {
    const { data, error } = await supabase
      .from('dados_bancarios')
      .select('*')
      .eq('pessoa_id', pessoaId)
      .order('id', { ascending: true });

    if (error) return [];
    const map = new Map<number, DadosBancarios>();
    (data || []).forEach((d) => {
      if (d && d.id != null) {
        map.set(Number(d.id), {
          ...d,
          nome: d.nome ? sanitizeText(d.nome) : d.nome,
        });
      }
    });
    return Array.from(map.values());
  },

  async getTiposConta(): Promise<TipoConta[]> {
    const { data, error } = await supabase.from('tipo_conta').select('*').order('id', { ascending: true });
    if (error) return [];
    const map = new Map<number, TipoConta>();
    (data || []).forEach((t) => {
      if (t && t.id != null) map.set(Number(t.id), sanitizeTipoConta(t));
    });
    return Array.from(map.values());
  },

  async getFormasPagamento(): Promise<FormaPagamento[]> {
    const { data, error } = await supabase.from('forma_pagamento').select('*').order('id', { ascending: true });
    if (error) return [];
    const map = new Map<number, FormaPagamento>();
    (data || []).forEach((f) => {
      if (f && f.id != null) map.set(Number(f.id), sanitizeFormaPagamento(f));
    });
    return Array.from(map.values());
  },

  async getCategorias(): Promise<Categoria[]> {
    const { data, error } = await supabase.from('categoria').select('*').order('id', { ascending: true });
    if (error) return [];
    const map = new Map<number, Categoria>();
    (data || []).forEach((c) => {
      if (c && c.id != null) map.set(Number(c.id), sanitizeCategoria(c));
    });
    return Array.from(map.values());
  },

  async getContasAbertasByPessoa(pessoaId: number, tipoContaId: number): Promise<Conta[]> {
    const { data, error } = await supabase
      .from('conta')
      .select('*, categoria(nome), forma_pagamento(nome)')
      .eq('pessoa_id', pessoaId)
      .eq('tipo_conta_id', tipoContaId)
      .is('dt_pagamento', null)
      .order('dt_vencimento', { ascending: true });

    if (error) throw error;
    return (data || []).map(enrichConta).map(sanitizeConta);
  }
};
